/**
 * One-Euro filter (Casiez, Roussel & Vogel, CHI 2012).
 *
 * MediaPipe landmarks jitter by a couple of pixels per frame; at 3 fps that
 * jitter turns into ±2–3° of angle noise, which is half of our ±5° MDC band.
 * One-Euro removes it while keeping fast, genuine movement responsive — a plain
 * low-pass would smear the moment a user slumps.
 *
 * Defaults come from docs/ARCHITECTURE.md §1.2: minCutoff 1.0, beta 0.007,
 * dCutoff 1.0.
 */

export interface OneEuroOptions {
  minCutoff: number;
  beta: number;
  dCutoff: number;
}

export const ONE_EURO_DEFAULTS: OneEuroOptions = {
  minCutoff: 1.0,
  beta: 0.007,
  dCutoff: 1.0,
};

function alpha(cutoff: number, dt: number): number {
  const tau = 1 / (2 * Math.PI * cutoff);
  return 1 / (1 + tau / dt);
}

class LowPass {
  private y: number | null = null;

  filter(value: number, a: number): number {
    this.y = this.y === null ? value : a * value + (1 - a) * this.y;
    return this.y;
  }

  reset(): void {
    this.y = null;
  }
}

/** Scalar One-Euro filter. `timestampMs` must be monotonically increasing. */
export class OneEuroFilter {
  private readonly x = new LowPass();
  private readonly dx = new LowPass();
  private lastTime: number | null = null;
  private lastValue: number | null = null;

  constructor(private readonly options: OneEuroOptions = ONE_EURO_DEFAULTS) {}

  filter(value: number, timestampMs: number): number {
    if (!Number.isFinite(value)) return this.lastValue ?? value;
    const dt =
      this.lastTime === null || timestampMs <= this.lastTime
        ? 1 / 30
        : (timestampMs - this.lastTime) / 1000;
    this.lastTime = timestampMs;

    const previous = this.lastValue;
    const rawDerivative = previous === null ? 0 : (value - previous) / dt;
    const derivative = this.dx.filter(rawDerivative, alpha(this.options.dCutoff, dt));
    const cutoff = this.options.minCutoff + this.options.beta * Math.abs(derivative);
    const filtered = this.x.filter(value, alpha(cutoff, dt));
    this.lastValue = value;
    return filtered;
  }

  reset(): void {
    this.x.reset();
    this.dx.reset();
    this.lastTime = null;
    this.lastValue = null;
  }
}

export interface FilterableLandmark {
  x: number;
  y: number;
  z: number;
  visibility: number;
}

/**
 * Wraps a whole 33-landmark frame: x, y and z are filtered independently per
 * landmark (visibility is passed through — smoothing a confidence would hide
 * exactly the dropouts the quality gate needs to see).
 */
export class LandmarkSmoother {
  private filters: OneEuroFilter[][] = [];

  constructor(private readonly options: OneEuroOptions = ONE_EURO_DEFAULTS) {}

  private ensure(count: number): void {
    while (this.filters.length < count) {
      this.filters.push([
        new OneEuroFilter(this.options),
        new OneEuroFilter(this.options),
        new OneEuroFilter(this.options),
      ]);
    }
  }

  smooth<T extends FilterableLandmark>(landmarks: readonly T[], timestampMs: number): T[] {
    this.ensure(landmarks.length);
    return landmarks.map((lm, i) => {
      const trio = this.filters[i]!;
      return {
        ...lm,
        x: trio[0]!.filter(lm.x, timestampMs),
        y: trio[1]!.filter(lm.y, timestampMs),
        z: trio[2]!.filter(lm.z, timestampMs),
      };
    });
  }

  reset(): void {
    for (const trio of this.filters) for (const f of trio) f.reset();
  }
}

/**
 * Rolling median over a time window — the second smoothing stage from
 * ARCHITECTURE §1.2. Posture is a slow signal, so a 1 s median kills the
 * occasional landmark flip that One-Euro lets through.
 */
export class RollingMedian {
  private readonly samples: Array<{ t: number; v: number }> = [];

  constructor(private readonly windowMs: number) {}

  push(value: number, timestampMs: number): number {
    this.samples.push({ t: timestampMs, v: value });
    const cutoff = timestampMs - this.windowMs;
    while (this.samples.length > 0 && this.samples[0]!.t < cutoff) this.samples.shift();
    const sorted = this.samples.map((s) => s.v).sort((a, b) => a - b);
    // `samples` always holds at least the value just pushed.
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 === 1
      ? sorted[mid]!
      : (sorted[mid - 1]! + sorted[mid]!) / 2;
  }

  reset(): void {
    this.samples.length = 0;
  }
}
